import Add from "@mui/icons-material/Add";
import { Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { useAttachPaymentToReport } from "hooks/usePayment";

import { paymentSectionColumns, PaymentSectionRow } from "./lib/paymentSectionColumns";

export function FacilityOnlyPaymentsSection({
  payments,
  reportId,
  reportingYear,
  onAttached,
}: {
  payments: PaymentSectionRow[];
  reportId: string;
  reportingYear: number;
  onAttached?: () => void;
}) {
  const theme = useTheme();
  const [attach, { loading }] = useAttachPaymentToReport();

  const handleAttach = async (paymentId: string) => {
    await attach({ variables: { paymentId, reportId } });
    onAttached?.();
  };

  return (
    <Stack spacing={theme.spacing(1)}>
      <Typography variant="h6">Facility-only payments on this facility</Typography>
      <Typography variant="body2" color="text.secondary">
        These payments are attached to the facility and to no report or submission. Attach one to
        this report if it was misfiled.
      </Typography>
      <DataGrid<PaymentSectionRow>
        hideFooter
        disableRowSelectionOnClick
        rows={payments}
        initialState={{ sorting: { sortModel: [{ field: "paidAt", sort: "desc" }] } }}
        columns={[
          ...paymentSectionColumns,
          {
            type: "actions" as const,
            field: "actions",
            align: "left" as const,
            getActions: ({ row }) => {
              const paidYear = new Date(row.paidAt).getUTCFullYear();
              const tooEarly = paidYear <= reportingYear;
              return [
                <GridActionsCellItem
                  key="attach"
                  style={{ pointerEvents: "all" }}
                  icon={
                    <Tooltip
                      title={
                        tooEarly
                          ? `Paid in ${paidYear}; cannot belong to RY ${reportingYear} (payments are made the following year)`
                          : "Attach to this report"
                      }
                    >
                      <Add />
                    </Tooltip>
                  }
                  label="Attach to report"
                  disabled={tooEarly || loading}
                  onClick={() => handleAttach(row.id)}
                />,
              ];
            },
          },
        ]}
      />
    </Stack>
  );
}
